import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Button,
  Paper,
  InputAdornment,
  TextField,
  Fade,
  Grow,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';

const BASE_URL = 'https://dhahabjw.com';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [refreshing, setRefreshing] = useState(false);

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const adminToken = localStorage.getItem('adminToken');

  const fetchDashboardData = async (selectedTimeRange = timeRange) => {
    try {
      setRefreshing(true);
      setError(null);

      const response = await axios.get(
        `${BASE_URL}/api/admin/dashboard/stats?timeRange=${selectedTimeRange}`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setDashboardData(response.data.data);
        setSuccess('Dashboard refreshed successfully');
        setTimeout(() => setSuccess(null), 2000);
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchDashboardData();
    } else {
      setError('Authentication required');
      setLoading(false);
    }
  }, [timeRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      processing: 'primary',
      shipped: 'secondary',
      delivered: 'success',
      cancelled: 'error',
      refunded: 'error'
    };
    return colors[status] || 'default';
  };

  const timeRangeLabels = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '1y': 'Last year'
  };

  const hasActiveFilters = Object.values(filters).some(
    value => value !== '' && value !== 'createdAt' && value !== 'desc'
  );

  const getFilteredOrders = () => {
    if (!dashboardData?.charts?.recentOrders) return [];
    
    let filtered = [...dashboardData.charts.recentOrders];

    if (filters.search) {
      filtered = filtered.filter(order =>
        order.orderNumber.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.userId?.email?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    return filtered;
  };

  const getFilteredProducts = () => {
    if (!dashboardData?.charts?.topProducts) return [];
    
    let filtered = [...dashboardData.charts.topProducts];

    if (filters.search) {
      filtered = filtered.filter(product =>
        product.productName.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    return filtered;
  };

  // Light Theme Metric Card
  const MetricCard = ({ title, value, subtitle, icon, gradient, chip, trend }) => (
    <Grow in={true} timeout={800}>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 24px rgba(218, 165, 32, 0.15)',
            border: '1px solid #DAA520',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: gradient,
          }
        }}
      >
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontWeight: 600,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.25rem' }
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#888',
                    fontSize: '0.875rem'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {chip && (
                <Chip
                  label={chip}
                  size="small"
                  icon={trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  sx={{
                    mt: 2,
                    bgcolor: trend === 'up' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    color: trend === 'up' ? '#4caf50' : '#f44336',
                    fontWeight: 600,
                    border: `1px solid ${trend === 'up' ? '#4caf50' : '#f44336'}`,
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                width: { xs: 50, sm: 64 },
                height: { xs: 50, sm: 64 },
                bgcolor: 'rgba(218, 165, 32, 0.1)',
                border: '2px solid #DAA520',
                boxShadow: '0 4px 12px rgba(218, 165, 32, 0.2)'
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 32, color: '#DAA520' } })}
            </Avatar>
          </Box>
        </CardContent>

        {/* Decorative gradient */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${gradient.split(',')[0].replace('linear-gradient(', '')} 0%, transparent 70%)`,
            opacity: 0.1,
            pointerEvents: 'none'
          }}
        />
      </Card>
    </Grow>
  );

  // Light Theme Status Card
  const StatusCard = ({ status, count, amount }) => (
    <Fade in={true} timeout={600}>
      <Card
        sx={{
          height: '100%',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            border: '1px solid #DAA520',
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(218, 165, 32, 0.15)'
          }
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Chip
              label={status}
              color={getStatusColor(status)}
              size="small"
              sx={{ 
                textTransform: 'capitalize',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#DAA520' }}>
              {count}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1.5, bgcolor: '#F0F0F0' }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 600 }}>
            {formatCurrency(amount)}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)'
        }}
      >
        <CircularProgress 
          size={60} 
          sx={{ 
            color: '#DAA520',
            mb: 3
          }} 
        />
        <Typography variant="h6" sx={{ color: '#DAA520', fontWeight: 600 }}>
          Loading Dashboard...
        </Typography>
        <LinearProgress 
          sx={{ 
            width: 300, 
            mt: 2,
            bgcolor: 'rgba(218, 165, 32, 0.1)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#DAA520'
            }
          }} 
        />
      </Box>
    );
  }

  const filteredOrders = getFilteredOrders();
  const filteredProducts = getFilteredProducts();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 50%, #F8F9FA 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(218, 165, 32, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(218, 165, 32, 0.03) 0%, transparent 50%)
          `,
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Elegant Header */}
        <Box sx={{ mb: 5 }}>
          <Fade in={true} timeout={500}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: 3
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(218, 165, 32, 0.1)',
                    border: '3px solid #DAA520',
                    boxShadow: '0 4px 12px rgba(218, 165, 32, 0.2)'
                  }}
                >
                  <AssessmentIcon sx={{ fontSize: 36, color: '#DAA520' }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#1A1A1A',
                      mb: 0.5
                    }}
                  >
                    Dashboard Overview
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    Welcome back, <strong>{adminUser.username || 'Admin'}</strong> • Monitor your business performance
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Fade>
        </Box>

        {/* Controls Section */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            background: '#FFFFFF',
            border: '1px solid #E0E0E0',
            borderRadius: 3
          }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    label="Time Range"
                    onChange={handleTimeRangeChange}
                  >
                    <MenuItem value="7d">Last 7 days</MenuItem>
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="90d">Last 90 days</MenuItem>
                    <MenuItem value="1y">Last year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#DAA520' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status Filter"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="shipped">Shipped</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Refresh Dashboard">
                    <Button
                      onClick={() => fetchDashboardData(timeRange)}
                      disabled={refreshing}
                      variant="contained"
                      fullWidth
                      startIcon={refreshing ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : <RefreshIcon />}
                      sx={{
                        bgcolor: '#DAA520',
                        color: '#FFF',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: '#C8941B',
                          boxShadow: '0 4px 12px rgba(218, 165, 32, 0.3)'
                        }
                      }}
                    >
                      {refreshing ? 'Refreshing' : 'Refresh'}
                    </Button>
                  </Tooltip>
                  {hasActiveFilters && (
                    <Tooltip title="Clear Filters">
                      <IconButton
                        onClick={handleClearFilters}
                        sx={{
                          border: '1px solid #DAA520',
                          color: '#DAA520',
                          '&:hover': {
                            bgcolor: 'rgba(218, 165, 32, 0.1)'
                          }
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Alerts */}
        {error && (
          <Fade in={true}>
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {success && (
          <Fade in={true}>
            <Alert
              severity="success"
              onClose={() => setSuccess('')}
              sx={{ mb: 3 }}
            >
              {success}
            </Alert>
          </Fade>
        )}

        {/* Key Metrics */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            mb: 3,
            color: '#1A1A1A'
          }}
        >
          Key Performance Indicators
        </Typography>
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Users"
              value={dashboardData?.overview.users.total || 0}
              subtitle={`${dashboardData?.overview.users.active || 0} active users`}
              icon={<PeopleIcon />}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              chip={`+${dashboardData?.overview.users.growth || 0} new`}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Products"
              value={dashboardData?.overview.products.total || 0}
              subtitle={`${dashboardData?.overview.products.active || 0} active products`}
              icon={<InventoryIcon />}
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Categories"
              value={dashboardData?.overview.stores.total || 0}
              subtitle={`${dashboardData?.overview.stores.active || 0} active`}
              icon={<CategoryIcon />}
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              title="Total Revenue"
              value={formatCurrency(dashboardData?.overview.orders.revenue || 0)}
              subtitle={`${dashboardData?.overview.orders.total || 0} orders • Avg: ${formatCurrency(dashboardData?.overview.orders.averageValue || 0)}`}
              icon={<MoneyIcon />}
              gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              trend="up"
            />
          </Grid>
        </Grid>

        {/* Orders by Status */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            mb: 3,
            color: '#1A1A1A'
          }}
        >
          Order Status Breakdown
        </Typography>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {dashboardData?.charts.ordersByStatus?.map((status) => (
            <Grid item xs={12} sm={6} md={2.4} key={status._id}>
              <StatusCard
                status={status._id}
                count={status.count}
                amount={status.totalAmount}
              />
            </Grid>
          ))}
        </Grid>

        {/* Data Tables */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                      width: 48,
                      height: 48,
                      border: '2px solid #DAA520'
                    }}
                  >
                    <TrendingUpIcon sx={{ color: '#DAA520' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
                      Top Selling Products
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filteredProducts.length} products
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2, bgcolor: '#F0F0F0' }} />

                {filteredProducts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <InventoryIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 2 }} />
                    <Typography color="text.secondary" variant="h6">
                      No products found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'No product data available'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Product</TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredProducts.map((product, index) => (
                          <TableRow
                            key={product._id}
                            sx={{
                              '&:hover': {
                                bgcolor: 'rgba(218, 165, 32, 0.05)'
                              }
                            }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {product.productName}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={product.totalQuantity} 
                                size="small" 
                                sx={{ 
                                  bgcolor: 'rgba(218, 165, 32, 0.1)', 
                                  color: '#DAA520',
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {formatCurrency(product.totalRevenue)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card
              elevation={0}
              sx={{
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                      width: 48,
                      height: 48,
                      border: '2px solid #DAA520'
                    }}
                  >
                    <ShoppingCartIcon sx={{ color: '#DAA520' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
                      Recent Orders
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filteredOrders.length} orders
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2, bgcolor: '#F0F0F0' }} />

                {filteredOrders.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <ShoppingCartIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 2 }} />
                    <Typography color="text.secondary" variant="h6">
                      No orders found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'No order data available'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Order #</TableCell>
                          <TableCell sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Customer</TableCell>
                          <TableCell sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Status</TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#F8F9FA', fontWeight: 700 }}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOrders.map((order) => (
                          <TableRow
                            key={order._id}
                            sx={{
                              '&:hover': {
                                bgcolor: 'rgba(218, 165, 32, 0.05)'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#DAA520' }}>
                                {order.orderNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {order.userId?.profile?.firstName} {order.userId?.profile?.familyName}
                              </Typography>
                              {!isMobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {order.userId?.email}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status}
                                color={getStatusColor(order.status)}
                                size="small"
                                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatCurrency(order.pricing?.totalAmount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box sx={{ mt: 6 }}>
          <Divider sx={{ mb: 3, bgcolor: '#E0E0E0' }} />
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 2
            }}
          >
            <Stack direction="row" spacing={3} justifyContent="center" alignItems="center" flexWrap="wrap">
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 16, color: '#DAA520' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Last updated: {dashboardData?.lastUpdated ? formatDate(dashboardData.lastUpdated) : 'N/A'}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: '#E0E0E0' }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <AssessmentIcon sx={{ fontSize: 16, color: '#DAA520' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Data range: {timeRangeLabels[dashboardData?.timeRange || timeRange]}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
